// Vendored from @eduact/ed-components@0.4.10 `Test/EssayQuestion/EssayQuestion`
// (see VENDORED.md for the full list of deltas). The text answer is written in
// the rich text answer editor (RichTextAnswer) instead of the package's
// debounced plain <textarea>; the emitted payload declares what it carries
// (`{ answer, type: 'text', format: 'html' | 'plain' }`) and the API
// normalises it to stored HTML. Visuals unchanged.
import { Box, FlexLayout, Typography } from '@eduact/ed-system'
import { Dialog, Icon, Icons, RaisedButton } from '@eduact/ed-components'
import Spacer from '@eduact/ed-components/Spacer'
import { Progress } from '@eduact/ed-components/Feedback/Progress'
import AttachmentAnswer from '@eduact/ed-components/Test/EssayQuestion/AttachmentAnswer'
import type { IEssayQuestion, UploadProgressArgs } from '@eduact/ed-components/Test/Question.types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import RichTextAnswer, { RichTextAnswerController } from '@Components/RichTextAnswer/RichTextAnswer'
import { parseAttachmentAnswer, serializeAttachmentAnswer } from './attachmentAnswer'
import { QuestionContentWrapper } from './QuestionStyled'

/**
 * `format` says what the client produced for a TEXT answer — `html` from the
 * rich editor, `plain` from the textarea fallback — so the API can sanitize or
 * escape it. Always sent by this app; a served answer is HTML, so anything
 * re-sent unchanged declares `html`.
 */
export type EssayAnswerPayload = { type: 'text' | 'attachment'; answer: string | null; format?: 'html' | 'plain' }

/** Handed to the view mode so a Next / Submit can flush the editor's debounce first. */
export type EssayAnswerControl = { flush(): unknown; cancel(): void }

export type EssayProps = {
	question: IEssayQuestion
	onChange: (answer: EssayAnswerPayload) => void
	chooseTypeTitle?: string
	orLabel?: string
	onAttachmentsChange?: (files: FileList | null) => void
	uploadProgress?: UploadProgressArgs
	textChangeDelay?: number
	/** Called once on mount with the editor control and with null on unmount. */
	onAnswerControl?: (control: EssayAnswerControl | null) => void
} & Pick<React.HTMLProps<HTMLInputElement>, 'accept'>

const EssayQuestion: React.VoidFunctionComponent<EssayProps> = ({
	onChange,
	onAttachmentsChange,
	onAnswerControl,
	uploadProgress,
	question,
	chooseTypeTitle = 'Choose your form of answer',
	orLabel = 'Or',
	accept,
	textChangeDelay = 5000,
}) => {
	const [answerType, setAnswerType] = useState<'text' | 'attachment' | undefined>(
		question.answer && question.answer.type
			? question.answer.type
			: (question as any).answer_schema === '*'
			? undefined
			: (question as any).answer_schema
	)
	const otherOption = useMemo(() => (answerType === 'attachment' ? 'text' : 'attachment'), [answerType])

	const value = useMemo(() => {
		if (!question.answer) return undefined
		if (question.answer.type === 'attachment') return parseAttachmentAnswer(question.answer.answer)
		return question.answer.answer
	}, [question.answer])

	// The editor's controller (null while the chunk loads / after a fallback —
	// flush then simply reports nothing pending). Registered upward once, as a
	// stable delegate, so switching to attachment mode needs no re-registration.
	const editorRef = useRef<RichTextAnswerController | null>(null)
	const onAnswerControlRef = useRef(onAnswerControl)
	onAnswerControlRef.current = onAnswerControl
	useEffect(() => {
		const control: EssayAnswerControl = {
			flush: () => editorRef.current?.flush(),
			cancel: () => editorRef.current?.cancel(),
		}
		onAnswerControlRef.current?.(control)
		return () => onAnswerControlRef.current?.(null)
	}, [])

	const [switchModalOpen, setSwitchModalOpen] = useState(false)
	const changeType = () => {
		setSwitchModalOpen(false)
		// Drop whatever the editor still holds in its debounce BEFORE the switch:
		// its unmount flush runs after this commit and would re-emit the erased
		// text as a `text` answer over the cleared attachment answer.
		editorRef.current?.cancel()
		if (answerType === 'attachment') {
			setAnswerType('text')
			onChange({ answer: null, type: 'text' })
			return
		}
		setAnswerType('attachment')
		onChange({ answer: null, type: 'attachment' })
	}

	const progress = Math.floor((uploadProgress?.progress?.progress ?? 0) * 100)

	return (
		<Spacer>
			<QuestionContent dangerouslySetInnerHTML={{ __html: question.content }} />
			<AnswersLabel fontSize={{ sm: '0.5rem', lg: '1rem' }}>Answer</AnswersLabel>
			<Dialog maxWidth={'90vw'} width={'fit-content'} height={'fit-content'} onClose={() => setSwitchModalOpen(false)} open={switchModalOpen}>
				<FlexLayout p={'2rem'} flexDirection={'column'}>
					<FlexLayout p={'2rem'} flexDirection={'column'}>
						<Typography>Are you sure to switch answer type ?</Typography>
						<Typography>NB: Answer will be erased</Typography>
					</FlexLayout>
					<FlexLayout gridGap={{ sm: '0.5rem', lg: '2rem' }} flexWrap={{ sm: 'wrap' }} justifyContent={{ sm: 'flex-end', lg: 'initial' }}>
						<RaisedButton onClick={changeType} btnSize="small">
							Switch
						</RaisedButton>
						<RaisedButton onClick={() => setSwitchModalOpen(false)} variant="princetonOrange" btnSize="small" outlined>
							Cancel
						</RaisedButton>
					</FlexLayout>
				</FlexLayout>
			</Dialog>
			<Spacer mb={{ sm: '0.813rem', lg: '2.313rem' }} />
			<FlexLayout flexDirection="column">
				<Spacer mb={{ sm: '6px', lg: '1rem' }} />
				{!answerType && (question as any).answer_schema === '*' && (
					<FlexLayout width={'100%'} justifyContent={'center'}>
						<Box width={'fit-content'}>
							<ChooseTypeTitle>{chooseTypeTitle}</ChooseTypeTitle>
							<FlexLayout justifyContent={'space-between'}>
								<AnswerOptionWrapper>
									<IconThumbnail onClick={() => setAnswerType('text')} as="button">
										<Icon>
											<Icons.Text />
										</Icon>
									</IconThumbnail>
									<IconLabel>Text</IconLabel>
								</AnswerOptionWrapper>
								<OrLabel>{orLabel}</OrLabel>
								<AnswerOptionWrapper>
									<IconThumbnail onClick={() => setAnswerType('attachment')} as="button">
										<Icon>
											<Icons.Attachment />
										</Icon>
									</IconThumbnail>
									<IconLabel>Attachment</IconLabel>
								</AnswerOptionWrapper>
							</FlexLayout>
						</Box>
					</FlexLayout>
				)}
				{answerType && (question as any).answer_schema == '*' && (
					<SwitchButton onClick={() => setSwitchModalOpen(true)} mb={2} justifyContent={'flex-end'}>
						<SwitchTypeLabel>Switch to {otherOption}</SwitchTypeLabel>
						{answerType === 'text' && (
							<Icon size={'1.2rem'}>
								<Icons.Attachment />
							</Icon>
						)}
						{answerType === 'attachment' && (
							<Icon size={'1.2rem'}>
								<Icons.Text />
							</Icon>
						)}
					</SwitchButton>
				)}
				{answerType === 'text' && (
					<RichTextAnswer
						value={typeof value === 'string' ? value : ''}
						delay={textChangeDelay}
						placeholder="Type something here"
						controllerRef={editorRef}
						onChange={({ value, format }) => onChange({ answer: value === '' ? null : value, type: 'text', format })}
					/>
				)}
				{answerType === 'attachment' && (
					<>
						<FlexLayout gridGap={'0.5rem'} mb={4}>
							<Box visibility={uploadProgress ? 'visible' : 'hidden'}>
								<Progress value={progress} min="0" max="100">
									{progress}%
								</Progress>
							</Box>
							<Typography
								display={'block'}
								overflow={'hidden'}
								maxWidth={{ sm: '20ch', lg: '40ch' }}
								textOverflow="ellipsis"
								fontSize={{ sm: '0.7rem' }}
								fontWeight={600}
								whiteSpace={'nowrap'}
							>
								{uploadProgress?.file?.name}
							</Typography>
						</FlexLayout>
						<AttachmentAnswer
							accept={accept}
							value={value}
							onFileChange={(files: FileList | null) => onAttachmentsChange?.(files)}
							onDelete={(data: string[]) => onChange({ type: 'attachment', answer: serializeAttachmentAnswer(data) })}
						/>
					</>
				)}
			</FlexLayout>
		</Spacer>
	)
}

export default EssayQuestion

const AnswersLabel = styled(Typography)`
	color: ${props => props.theme.colors.silver};
	font-size: 1rem;
	font-weight: 300;
	@media screen and (min-width: 769px) {
		margin-bottom: 0.75rem;
	}
`
const QuestionContent = styled(QuestionContentWrapper)`
	margin-bottom: 1.5rem;
`
const ChooseTypeTitle = styled.div`
	font-size: 1.375rem;
	font-family: 'Roboto';
	font-weight: normal;
	letter-spacing: 0px;
	margin-bottom: 1.438rem;
`
const IconThumbnail = styled.div`
	width: 77.5px;
	height: 77.5px;
	border-radius: 5px;
	box-shadow: 0 5px 6px 0 rgba(0, 0, 0, 0.16);
	background: ${props => props.theme.colors.light};
	border: solid 1px #c4c4c4;
	font-size: 3.5rem;
	display: grid;
	place-content: center;
	margin-bottom: 0.75rem;
	cursor: pointer;
`
const IconLabel = styled.span`
	font-size: 1.125rem;
	color: #707070;
	font-weight: 500;
	font-family: Montserrat;
`
const AnswerOptionWrapper = styled(FlexLayout)`
	max-width: 77.5px;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`
const OrLabel = styled.span`
	font-size: 1.25rem;
	color: #707070;
	margin: 0 2rem;
	margin-top: 2rem;
`
const SwitchTypeLabel = styled.span`
	color: #adadad;
	text-decoration: underline;
	text-transform: capitalize;
`
const SwitchButton = styled(FlexLayout)`
	cursor: pointer;
`
